import { useEffect, useState } from 'react';
import { Activity, TrendingUp, Languages, Send, BrainCircuit, Sparkles } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

/* ──────────────────────────────────────────────────────────────────
 * HeroDashboardDemo
 * Investor-demo-ready animated showcase of "Quidory in motion".
 * - Bilingual content (mix of AR + EN signals to communicate
 *   multilingual intelligence at a glance)
 * - Pure CSS keyframes for sparkline / pulse / shimmer
 * - Single setInterval to rotate the AI-agent status pill
 * - Direction-aware (RTL-safe via logical props)
 * ──────────────────────────────────────────────────────────────── */

// Each feed signal mixes a quick localized headline + delta + direction
const signalsPool = [
  { lang: 'ar', tag: 'BTC',  text: 'تجاوز ٩٥٠٠٠$ — كسر مقاومة قوي',         delta: '+2.3%', up: true,  source: 'QuidPulse' },
  { lang: 'en', tag: 'ETH',  text: 'Accumulation pattern detected on 4h',     delta: '-0.8%', up: false, source: 'Quidanalyzer' },
  { lang: 'ar', tag: 'SOL',  text: 'حجم تداول مرتفع — اتجاه صعودي',           delta: '+4.1%', up: true,  source: 'QuidPulse' },
  { lang: 'en', tag: 'AVAX', text: 'Whale wallets net-buying past 24h',       delta: '+3.7%', up: true,  source: 'Quidanalyzer' },
  { lang: 'ar', tag: 'BNB',  text: 'دعم رئيسي عند ٤٨٥$ — مراقبة',             delta: '-1.2%', up: false, source: 'QuidPulse' },
  { lang: 'en', tag: 'LINK', text: 'Institutional inflows at 6-month high',   delta: '+2.8%', up: true,  source: 'Quidanalyzer' },
];

const HeroDashboardDemo = () => {
  const { language, isRTL } = useLanguage();
  const isAr = language === 'ar';

  // Rotating signal window (3 visible)
  const [tick, setTick] = useState(0);
  // Rotating agent status pill
  const [agentIdx, setAgentIdx] = useState(0);

  const agentStates = isAr
    ? [
        { icon: BrainCircuit, label: 'تحليل السوق ...',           color: 'text-indigo-300' },
        { icon: Send,         label: 'نشر إلى تيليجرام',          color: 'text-emerald-300' },
        { icon: Sparkles,     label: 'توليد رؤى جديدة',           color: 'text-gold' },
        { icon: Languages,    label: 'ترجمة إلى الإنجليزية',      color: 'text-purple-300' },
      ]
    : [
        { icon: BrainCircuit, label: 'Analyzing market...',       color: 'text-indigo-300' },
        { icon: Send,         label: 'Publishing to Telegram',    color: 'text-emerald-300' },
        { icon: Sparkles,     label: 'Generating new insights',   color: 'text-gold' },
        { icon: Languages,    label: 'Translating to Arabic',     color: 'text-purple-300' },
      ];

  useEffect(() => {
    const t1 = setInterval(() => setTick(n => n + 1), 2600);
    const t2 = setInterval(() => setAgentIdx(n => (n + 1) % agentStates.length), 2200);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, [agentStates.length]);

  // pick 3 sliding signals
  const visible = Array.from({ length: 3 }, (_, i) => signalsPool[(tick + i) % signalsPool.length]);

  const Agent = agentStates[agentIdx];

  // Mini stats with subtle counter animation via key remount
  const stats = isAr
    ? [
        { label: 'إشارات نشطة', value: 1247, suffix: '', accent: 'text-gold' },
        { label: 'اللغات',      value: 2,    suffix: '', accent: 'text-indigo-300' },
        { label: 'منشورات',     value: 184,  suffix: '/يوم', accent: 'text-emerald-300' },
      ]
    : [
        { label: 'Active signals',  value: 1247, suffix: '',     accent: 'text-gold' },
        { label: 'Languages live',  value: 2,    suffix: '',     accent: 'text-indigo-300' },
        { label: 'Posts published', value: 184,  suffix: '/day', accent: 'text-emerald-300' },
      ];

  return (
    <div
      data-testid="hero-dashboard-demo"
      className="relative"
      aria-hidden="true"
    >
      {/* Outer glow */}
      <div className="absolute -inset-6 bg-gradient-to-br from-indigo-500/15 via-purple-500/10 to-gold/10 rounded-3xl blur-2xl" />

      {/* Frame */}
      <div className="relative rounded-2xl border border-border/60 bg-card/80 backdrop-blur-xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] overflow-hidden">
        {/* Top gold accent line */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent" />

        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/60 bg-background/40">
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-red-400/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-yellow-400/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
          </div>
          <div className="flex-1 mx-3 px-3 py-1 rounded-md border border-border/40 bg-card/50 text-[10px] text-muted-foreground/70 font-mono truncate text-center">
            app.quidory.com / dashboard
          </div>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-emerald-400 uppercase tracking-wider">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
            </span>
            {isAr ? 'مباشر' : 'Live'}
          </span>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-5 space-y-4" dir={isRTL ? 'rtl' : 'ltr'}>
          {/* Heading row */}
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gold/80">
                {isAr ? 'لوحة الذكاء' : 'Intelligence Console'}
              </p>
              <h3 className="mt-0.5 text-sm sm:text-base font-semibold text-foreground truncate">
                {isAr ? 'كويدوري — مباشر' : 'Quidory — Operational'}
              </h3>
            </div>
            {/* Rotating Agent Status pill */}
            <div
              data-testid="hero-demo-agent-pill"
              key={agentIdx}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-border/60 bg-background/60 text-[10px] font-medium animate-in fade-in slide-in-from-bottom-1 duration-300"
            >
              <Agent.icon className={`h-3 w-3 ${Agent.color}`} />
              <span className="text-foreground/90 max-w-[140px] truncate">{Agent.label}</span>
            </div>
          </div>

          {/* Mini KPIs */}
          <div className="grid grid-cols-3 gap-2">
            {stats.map((s, i) => (
              <div
                key={i}
                className="p-2.5 rounded-lg border border-border/50 bg-background/40"
              >
                <p className="text-[10px] text-muted-foreground/80 truncate">{s.label}</p>
                <p className={`mt-1 text-base sm:text-lg font-semibold ${s.accent}`} dir="ltr">
                  {s.value.toLocaleString(isAr ? 'ar-EG' : 'en-US')}
                  <span className="ms-0.5 text-[10px] text-muted-foreground/60 font-normal">{s.suffix}</span>
                </p>
              </div>
            ))}
          </div>

          {/* Sparkline / live chart */}
          <div className="relative h-16 rounded-lg border border-border/50 bg-background/40 overflow-hidden">
            <div className="absolute inset-0 px-2 py-1.5 flex items-end justify-between gap-1">
              {/* 16 animated bars */}
              {Array.from({ length: 16 }).map((_, i) => (
                <span
                  key={i}
                  className="flex-1 rounded-sm bg-gradient-to-t from-gold/70 via-gold/40 to-transparent"
                  style={{
                    height: '40%',
                    animation: `hero-bar 2.4s ease-in-out ${i * 0.12}s infinite alternate`,
                  }}
                />
              ))}
            </div>
            {/* Sparkline overlay (svg) */}
            <svg
              viewBox="0 0 200 60"
              preserveAspectRatio="none"
              className="absolute inset-0 w-full h-full pointer-events-none"
            >
              <defs>
                <linearGradient id="heroLineGrad" x1="0" x2="1">
                  <stop offset="0%"   stopColor="hsl(45 80% 60%)" stopOpacity="0.0" />
                  <stop offset="40%"  stopColor="hsl(45 80% 60%)" stopOpacity="1" />
                  <stop offset="100%" stopColor="hsl(265 80% 65%)" stopOpacity="1" />
                </linearGradient>
              </defs>
              <path
                d="M0,42 L20,38 L40,32 L60,36 L80,26 L100,30 L120,18 L140,22 L160,14 L180,18 L200,8"
                fill="none"
                stroke="url(#heroLineGrad)"
                strokeWidth="1.5"
                style={{
                  strokeDasharray: '400',
                  strokeDashoffset: '400',
                  animation: 'hero-line-draw 3.6s ease-out infinite',
                }}
              />
            </svg>
            <span className="absolute top-1.5 end-2 text-[9px] font-mono text-gold/80 inline-flex items-center gap-1">
              <TrendingUp className="h-2.5 w-2.5" />
              +12.4%
            </span>
            <span className="absolute top-1.5 start-2 text-[9px] uppercase tracking-widest text-muted-foreground/70">
              {isAr ? 'حركة السوق · ٢٤س' : 'Market flow · 24h'}
            </span>
          </div>

          {/* Live signal feed */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground/70 inline-flex items-center gap-1.5">
                <Activity className="h-2.5 w-2.5 text-gold" />
                {isAr ? 'تدفق الإشارات' : 'Signal stream'}
              </p>
              <span className="text-[9px] text-muted-foreground/60 font-mono">
                {isAr ? 'يتحدّث الآن' : 'updating'}
              </span>
            </div>

            <div className="space-y-1.5">
              {visible.map((s, i) => (
                <div
                  key={`${tick}-${i}`}
                  data-testid={`hero-demo-signal-${i}`}
                  className="flex items-center gap-2 sm:gap-3 px-2.5 py-2 rounded-md border border-border/40 bg-background/30 animate-in slide-in-from-bottom-2 fade-in duration-500"
                  style={{ animationDelay: `${i * 70}ms`, animationFillMode: 'both' }}
                >
                  <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${s.up ? 'bg-emerald-400' : 'bg-red-400'}`} />
                  <span dir="ltr" className="font-mono text-[10px] font-bold text-foreground tabular-nums w-9 sm:w-10 shrink-0">
                    {s.tag}
                  </span>
                  <span
                    dir={s.lang === 'ar' ? 'rtl' : 'ltr'}
                    className={`flex-1 min-w-0 truncate text-[11px] text-foreground/80 ${s.lang === 'ar' ? 'font-arabic' : ''}`}
                  >
                    {s.text}
                  </span>
                  <span
                    dir="ltr"
                    className={`text-[10px] font-semibold tabular-nums ${s.up ? 'text-emerald-400' : 'text-red-400'} shrink-0`}
                  >
                    {s.delta}
                  </span>
                  <span className="hidden sm:inline text-[9px] text-muted-foreground/50 uppercase tracking-wider shrink-0">
                    {s.source}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Footer strip */}
          <div className="flex items-center justify-between pt-1.5 border-t border-border/40">
            <div className="flex items-center gap-2 text-[10px] text-muted-foreground/70">
              <Languages className="h-3 w-3 text-gold/80" />
              <span>{isAr ? 'عربي · إنجليزي' : 'AR · EN'}</span>
              <span className="text-border/60">·</span>
              <span>{isAr ? '٤ قنوات نشر' : '4 channels'}</span>
            </div>
            <div className="text-[10px] text-muted-foreground/60 font-mono">
              {isAr ? 'كل ٢٫٤ ث' : '2.4s tick'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroDashboardDemo;
